import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";
import { defaultWMS1141_R01DetailDto, defaultWMS1141_R01HeaderDto, WMS1141_R01DetailDto, WMS1141_R01HeaderDto } from './Dto_WMS1141_R01';
import { showWarning } from 'src/utils/toast/toastUtil';
import { createWMS1141_R01LotNo } from './Api_WMS1141_R01';
import { getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';


export interface WMS1141_R01Filter {
	dateRange: DateRange | undefined;
	wmsOrderNo: string;
	customCd: string;
	customNm: string;
	labelPrintAuto: string;
}

export interface Store_WMS1141_R01 {

	WMS1141_R01Filter: WMS1141_R01Filter;
	WMS1141_R01PastFilter: WMS1141_R01Filter;
	setWMS1141_R01Filter: (filter: Partial<Store_WMS1141_R01['WMS1141_R01Filter']>) => void;
	setWMS1141_R01PastFilter: (filter: Store_WMS1141_R01['WMS1141_R01PastFilter']) => void;

	// ====== 매출 리스트 및 선택/편집 ======
	WMS1141_R01List: WMS1141_R01HeaderDto[];
	setWMS1141_R01List: (dataList: WMS1141_R01HeaderDto[]) => void;

	editWMS1141_R01: WMS1141_R01HeaderDto | null;
	setEditWMS1141_R01: (editSale: WMS1141_R01HeaderDto | null) => void;
	
	selectedWMS1141_R01: WMS1141_R01HeaderDto | undefined;
	setSelectedWMS1141_R01: (data: WMS1141_R01HeaderDto | undefined) => void;
	initSelectedWMS1141_R01: () => void;

	// ====== 상세(디테일) 행 선택/삭제 관리 ======
	deletedWMS1141_R01DetailList: string[];
	setDeletedWMS1141_R01DetailList: (idList: string[]) => void;
	addDeletedWMS1141_R01DetailList: (idList: string[]) => void;
	removeDeletedWMS1141_R01DetailList: (idList?: string[]) => void;

	// ====== 매출 상세(디테일) 데이터 ======
	WMS1141_R01DetailList: WMS1141_R01DetailDto[];
	setWMS1141_R01DetailList: (data: WMS1141_R01DetailDto[]) => void;
	insertWMS1141_R01DetailList: (data: WMS1141_R01DetailDto[], index?: number) => void;
	removeWMS1141_R01DetailList: (index: number) => void;
	updateWMS1141_R01DetailList: (index: number, data: WMS1141_R01DetailDto) => void;

	// ====== 선택(디테일) 행 선택/삭제 관리 ======
	selectedWMS1141_R01DetailList: string[];
	setSelectedWMS1141_R01DetailList: (idList: string[]) => void;
	addSelectedWMS1141_R01DetailList: (idList: string[]) => void;
	removeSelectedWMS1141_R01DetailList: (idList?: string[]) => void;
	initSelectedWMS1141_R01DetailList: () => void;

	originalWMS1141_R01DetailList: WMS1141_R01DetailDto[];
	setOriginalWMS1141_R01DetailList: (data: WMS1141_R01DetailDto[]) => void;

	
	// ====== 컬럼 상태(그리드) ======
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;
		
	allCount: number;				setAllCount: (allCount: number) => void;
	detailFetchTrigger: boolean;	setDetailFetchTrigger: (trigger: boolean) => void;

	// ====== 초기화 ======
	initData: () => void;

	// 창고 코드 필터링 (로직센터 & 자재창고 여부)
	warehouseCdListToUseList: (logicCenter: string) => WareHouseDto[];
	// 공통코드 로드 후 초기값 동기화
	syncInitialData: () => void;
}

export const defaultWMS1141_R01Filter : WMS1141_R01Filter = {
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	wmsOrderNo: '',
	customCd: '',
	customNm: '',
	labelPrintAuto: 'N'
}

const getInitialWMS1141_R01HeaderDto = (): WMS1141_R01HeaderDto => {
	const { wmsCdList, warehouseCdList } = useCommonCodeStore.getState();
	return {
		...defaultWMS1141_R01HeaderDto,
		logicCenter: wmsCdList?.[0]?.minorCd || '',
		warehouseCd: warehouseCdList?.[0]?.warehouseCd || '',
	};
};

export const useWMS1141_R01Store = create<Store_WMS1141_R01>((set, get) => ({

	WMS1141_R01Filter: defaultWMS1141_R01Filter,
	WMS1141_R01PastFilter: defaultWMS1141_R01Filter,
	setWMS1141_R01Filter: (filter: Partial<Store_WMS1141_R01['WMS1141_R01Filter']>) => set((state) => ({
		...state,
		WMS1141_R01Filter: {
			...state.WMS1141_R01Filter,
			...filter
		}
	})),
	setWMS1141_R01PastFilter: (filter: Store_WMS1141_R01['WMS1141_R01PastFilter']) => set((state) => {
		return {
			...state,
			WMS1141_R01PastFilter: filter
		}
	}),

	// ====== 매출 리스트 및 선택/편집 ======
	WMS1141_R01List: [],
	setWMS1141_R01List: (dataList: WMS1141_R01HeaderDto[]) => set((state) => {
		return {
			...state,
			WMS1141_R01List: dataList
		}
	}),

	editWMS1141_R01: getInitialWMS1141_R01HeaderDto(),
	setEditWMS1141_R01: (editSale: WMS1141_R01HeaderDto | null) => set((state) => {
		return {
			...state,
			editWMS1141_R01: editSale
		}
	}),

	selectedWMS1141_R01: undefined,
	setSelectedWMS1141_R01: (data: WMS1141_R01HeaderDto | undefined) => set((state) => {

		return { 
			selectedWMS1141_R01: data, 
			selectedWMS1141_R01DetailList: [], deletedWMS1141_R01DetailList: [],
			detailFetchTrigger: true,
		};
	}),
	initSelectedWMS1141_R01: () => set({ 
		selectedWMS1141_R01: undefined, 
		editWMS1141_R01: getInitialWMS1141_R01HeaderDto(), 
		WMS1141_R01DetailList: [],
		detailFetchTrigger: false,
	}),

	// ====== 매출 상세(디테일) 데이터 ======
	WMS1141_R01DetailList: [],
	setWMS1141_R01DetailList: (data: WMS1141_R01DetailDto[]) => set((state) => {
		let newList: WMS1141_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item, index) => {
			// console.log(index + '번째 행의 item.Index 정보: ' 
			// 	+ ' valueIndex : ' + item.valueIndex 
			// 	+ ' valueAndOrderIndex : ' + valueAndOrderIndex
			// 	+ ' orderIndex : ' + item.orderIndex 
			// 	+ ' orderIndexSeq : ' + item.orderIndexSeq);
			newList.push({ 
				//...defaultWMS1141_R01DetailDto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			WMS1141_R01DetailList: newList
		}
	}),
	insertWMS1141_R01DetailList: async (data: WMS1141_R01DetailDto[], index?: number) => {
		const state = get();
		let newWMS1141_R01DetailList = [...state.WMS1141_R01DetailList];

		let targetRowData = newWMS1141_R01DetailList.find(item => item.valueIndex === index);
		let targetOrderIndex = targetRowData?.orderIndex || 0;
		// 데이터에 orderIndex가 있으면 그것을 사용하고, 없으면 1 (index 파라미터는 배열 위치로만 사용)		

		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		// console.log('targetOrderIndex : ', targetOrderIndex);
		// console.log('newOrderIndexSeq : ', newOrderIndexSeq);
		let maxValueIndex = Math.max(0, ...newWMS1141_R01DetailList.map(item => item.valueIndex || 0)) + 1;

		// newSYS4100_R01List에서 targetOrderIndex인 행들을 빼내서 newOrderIndexSeq 기준으로 다시 정렬
		// newOrderIndexSeq 보다 orderIndexSeq가 작은 행들은 유지. 
		// newOrderIndexSeq 보다 orderIndexSeq가 큰 행들은 기존 orderIndexSeq 값에 1을 더해서 저장.
		const targetRows = newWMS1141_R01DetailList.filter(item => item.orderIndex === targetOrderIndex);
		targetRows.forEach(item => {
			if(item.orderIndexSeq < newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq;
			} else if(item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		// targetRows를 newSYS4100_R01List에 대체
		newWMS1141_R01DetailList = newWMS1141_R01DetailList.filter(item => item.orderIndex !== targetOrderIndex);
		newWMS1141_R01DetailList.push(...targetRows);
		
		//새 행을 생성해서 newOrderIndexSeq 값의 행을 저장
		const newData = data.map(item => ({ ...defaultWMS1141_R01DetailDto, ...item, 
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq,
		}));
		// console.log('newData : ', newData);
		if (index !== undefined) {
			newWMS1141_R01DetailList.splice(index, 0, ...newData);
		} else {
			newWMS1141_R01DetailList.push(...newData);
		}

		set({
			WMS1141_R01DetailList: newWMS1141_R01DetailList
		});
	},
	removeWMS1141_R01DetailList: (index: number) => set((state) => {
		const newWMS1141_R01DetailList = [...state.WMS1141_R01DetailList].filter((item) => item.valueIndex !== index);
		return {
			...state,
			WMS1141_R01DetailList: newWMS1141_R01DetailList
		}
	}),
	updateWMS1141_R01DetailList: (valueIndex: number, data: WMS1141_R01DetailDto) => set((state) => {
		const _WMS1141_R01DetailList = state.WMS1141_R01DetailList.map(item =>
			item.valueIndex === valueIndex ? data : item
		);
		if(data.qty < 0) {
			showWarning('수량은 0보다 작을 수 없습니다.');
			data.qty = 0;
		}
		return {
			...state,
			WMS1141_R01DetailList: _WMS1141_R01DetailList
		}
	}),
	
	// ====== 상세(디테일) 행 선택/삭제 관리 ======
	deletedWMS1141_R01DetailList: [],
	setDeletedWMS1141_R01DetailList: (idList: string[]) => set((state) => {
		return {
			...state,
			deletedWMS1141_R01DetailList: idList
		}
	}),
	addDeletedWMS1141_R01DetailList: (idList: string[]) => set((state) => {
		const _deletedDetailList = [...state.deletedWMS1141_R01DetailList];

		for(const valueIndex of idList) {
			const isInserted = state.WMS1141_R01DetailList.find(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.WMS1141_R01DetailList.find(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if(!isInserted && !isModified) {
				_deletedDetailList.push(valueIndex);
			}
		}
		
		return {
			...state,
			deletedWMS1141_R01DetailList: _deletedDetailList
		}
	}),
	removeDeletedWMS1141_R01DetailList: (idList?: string[]) => set((state) => ({
		...state,
		deletedWMS1141_R01DetailList: [...state.deletedWMS1141_R01DetailList].filter(value => idList ? !idList.includes(value) : false)
	})),

	selectedWMS1141_R01DetailList: [],
	setSelectedWMS1141_R01DetailList: (idList: string[]) => set((state) => {
		return {
			...state,
			selectedWMS1141_R01DetailList: idList
		}
	}),
	addSelectedWMS1141_R01DetailList: (idList: string[]) => set((state) => {
		const _selectedWMS1141_R01DetailList = [...state.selectedWMS1141_R01DetailList];
		if(idList.length > 0) {
			_selectedWMS1141_R01DetailList.push(...idList);
		}
		return {
			...state,
			selectedWMS1141_R01DetailList: _selectedWMS1141_R01DetailList
		}
	}),
	removeSelectedWMS1141_R01DetailList: (idList?: string[]) => set((state) => ({
		...state,
		selectedWMS1141_R01DetailList: [...state.selectedWMS1141_R01DetailList].filter(value => idList ? !idList.includes(value) : false)
	})),
	initSelectedWMS1141_R01DetailList: () => set({ selectedWMS1141_R01DetailList: [] }),

	originalWMS1141_R01DetailList: [],
	setOriginalWMS1141_R01DetailList: (data: WMS1141_R01DetailDto[]) => set((state) => {
		let newList: WMS1141_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item, index) => {
			newList.push({ 
				...defaultWMS1141_R01DetailDto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalWMS1141_R01DetailList: newList
		}
	}),

	// ====== 컬럼 상태(그리드) ======
	columnState: null,
	setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),
	detailColumnState: null,
	setDetailColumnState: (state: ColumnState[] | null) => set({ detailColumnState: state }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),

	allCount: 0,				setAllCount: (allCount: number) => set({ allCount }),
	detailFetchTrigger: false,	setDetailFetchTrigger: (trigger: boolean) => set({ detailFetchTrigger: trigger }),

	// ====== 초기화 ======
	initData: () => set((state) => ({
		...state,
		WMS1141_R01Filter: defaultWMS1141_R01Filter,
		WMS1141_R01PastFilter: defaultWMS1141_R01Filter,
		WMS1141_R01List: [],
		selectedWMS1141_R01: undefined,
		WMS1141_R01DetailList: [],
		deletedWMS1141_R01DetailList: [],
		selectedWMS1141_R01DetailList: [],
		editWMS1141_R01: getInitialWMS1141_R01HeaderDto(),
		allCount: 0,
		detailFetchTrigger: false,
		scrollTop: null,
		detailScrollTop: null,
	})),

	warehouseCdListToUseList: (logicCenter: string) => {
		const { warehouseCdList } = useCommonCodeStore.getState();
		const filteredList = warehouseCdList.filter(value => (value.logicCenter === logicCenter
			&& (value.materialYn === 'Y')
		));
		
		if (filteredList.length === 0) {
			return [{ warehouseCd: '', warehouseNm: '' } as WareHouseDto];
		}
		
		return filteredList;
	},

	syncInitialData: () => {
		const { wmsCdList } = useCommonCodeStore.getState();
		const state = get();
		
		// 이미 값이 있거나 공통코드가 없으면 리턴
		if ((state.editWMS1141_R01?.logicCenter && state.editWMS1141_R01?.warehouseCd) || wmsCdList.length === 0) return;

		const defaultLogicCenter = wmsCdList?.[0]?.minorCd || '';
		
		// 스토어 내부 함수를 사용하여 유효한 창고 목록을 가져옴
		const availableWarehouses = state.warehouseCdListToUseList(defaultLogicCenter);
		const defaultWarehouseCd = availableWarehouses?.[0]?.warehouseCd || '';
		const defaultWarehouseNm = availableWarehouses?.[0]?.warehouseNm || '';

		set((state) => ({
			editWMS1141_R01: {
				...state.editWMS1141_R01!,
				logicCenter: defaultLogicCenter,
				warehouseCd: defaultWarehouseCd,
				warehouseNm: defaultWarehouseNm,
			}
		}));
	},

	
}));
